import { useContext, useEffect, useRef, useState } from 'react';
import Rupee from '../../../Assets/rupee.svg';
import ChevronDown from '../../../Assets/ChevronDownLargeDarkMode.svg';
import { InventoryEditProductFormContext } from '../../../Context/InventoryEditProductFormContext';
import { useOutsideClickForTwoRef } from '../../../Hooks/useOutsideClickForTwoRef';
import TickMark from '../../../Assets/Modals/TickMark.svg';
import { showToast } from '../../../Components/Toaster/Toaster';
import Percent from '../../../Assets/Modals/Percent.svg';
import Input from '../../../Components/Input/Input';

const gstCodes = [
    {
        id: 'GST_5',
        codeName: 'GST_5'
    },
    {
        id: 'GST_18',
        codeName: 'GST_18'
    },
    {
        id: 'GST_40',
        codeName: 'GST_40'
    },
]

function PricesAndTaxes() {
    const gstDropdownButtonRef = useRef(null);
    const gstDropdownRef = useRef(null);

    useOutsideClickForTwoRef(gstDropdownButtonRef, gstDropdownRef, () => {
        setGstDropdownOpen(false);
    });

    const { pricesAndTaxes, setPricesAndTaxes, formErrors } = useContext(InventoryEditProductFormContext);

    const handleChange = (e) => {
        const { name, value } = e.target;

        const mrp = Number(pricesAndTaxes.mrp);
        const discount = Number(value);

        if (name === "sellingPrice") {
            showToast("Selling Price is auto-calculated and cannot be changed directly", "warning");
            return;
        }

        if (name === "mrp") {
            if (value && Number(value) < 0) {
                showToast("MRP can't be negative", "warning");
                return;
            }
        }

        if (name === "discount") {
            if (isNaN(mrp) || mrp <= 0) {
                showToast("Please enter MRP before Discount", "warning");
                return;
            }

            if (value && !isNaN(discount) && discount > 100) {
                showToast("Discount can't be greater than 100", "warning");
                return;
            }
        }

        setPricesAndTaxes({ ...pricesAndTaxes, [name]: value });
    };

    const [gstDropdownOpen, setGstDropdownOpen] = useState(false);

    const toggleGstDropdown = () => {
        setGstDropdownOpen(!gstDropdownOpen);
    }

    const handleDropdownOptionClick = (e, option) => {
        e.stopPropagation();
        if(pricesAndTaxes.taxCode.id === option.id) {
            setPricesAndTaxes({ ...pricesAndTaxes, taxCode: {} })
        }
        else {
            setPricesAndTaxes({ ...pricesAndTaxes, taxCode: option });
        }
    }

    // Effect 1: Calculate selling price when MRP or discount changes
    useEffect(() => {
        if (pricesAndTaxes.mrp && pricesAndTaxes.mrp > 0) {
            if (pricesAndTaxes.discount && pricesAndTaxes.discount > 0) {
                const discountedPrice = pricesAndTaxes.mrp - (pricesAndTaxes.mrp * (pricesAndTaxes.discount / 100));
                setPricesAndTaxes(prev => ({ ...prev, sellingPrice: Math.round(discountedPrice) }));
            } else {
                setPricesAndTaxes(prev => ({ ...prev, sellingPrice: pricesAndTaxes.mrp }));
            }
        } else {
            setPricesAndTaxes(prev => ({ ...prev, sellingPrice: '', discount: '' }));
        }
        // eslint-disable-next-line
    }, [pricesAndTaxes.mrp, pricesAndTaxes.discount]);

    return (
        <div className="flex flex-col gap-4 border border-[#EAEAEA] p-5 rounded-xl">
            <h1 className='font-semibold text-[18px] leading-[26px]'>Price and Taxes</h1>

            <div className="grid grid-cols-5 gap-x-2 gap-y-4">
                <div className="flex flex-col gap-2 col-span-2">
                    <div className="flex items-center justify-between">
                        <label className='w-fit select-none cursor-pointer font-medium text-base text-[#121212]' htmlFor="mrp">MRP<span className="text-[#DC2626]">*</span></label>
                    
                        {
                            formErrors.mrp && 
                            <p className='text-[14px] leading-5 text-[#DC2626]'>{formErrors.mrp}</p>
                        }
                    </div>

                    <div className="h-14 flex items-center gap-1 px-2 py-[6px] bg-[#F5F5F5] border border-[#EAEAEA] rounded-lg focus-within:border-primaryColor">
                        <div className="w-6 h-6 flex items-center justify-center">
                            <img src={Rupee} alt="" />
                        </div>

                        <input 
                            id='mrp' 
                            name='mrp' 
                            className='w-[calc(100%-24px)] outline-none bg-transparent font-normal text-base text-[#121212]' 
                            type="number" 
                            placeholder='0.00' 
                            value={pricesAndTaxes.mrp} 
                            onChange={handleChange} 
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-2 col-span-1">
                    <div className="flex items-center justify-between">
                        <label className='w-fit select-none cursor-pointer font-medium text-base text-[#121212]' htmlFor="discount">Discount
                            <span className="text-[#DC2626]">*</span>
                        </label>
                    </div>

                    <div className="h-14 flex items-center gap-1 px-2 py-[6px] bg-[#F5F5F5] border border-[#EAEAEA] rounded-lg focus-within:border-primaryColor">
                        <input 
                            id='discount' 
                            name='discount' 
                            className='w-[calc(100%-24px)] outline-none bg-transparent font-normal text-base text-[#121212]' 
                            type="number" 
                            placeholder='00' 
                            value={pricesAndTaxes.discount} 
                            onChange={handleChange} 
                        />

                        <div className="w-6 h-6 flex items-center justify-center">
                            <img src={Percent} alt="" />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-2 col-span-2">
                    <div className="flex items-center justify-between">
                        <label className='w-fit select-none cursor-pointer font-medium text-base text-[#121212]' htmlFor="sellingPrice">Your Selling Price
                        <span className='text-[12px] leading-6 text-[#666666]'>&#40;Includes GST&#41;</span>
                        <span className="text-[#DC2626]">*</span></label>

                        {
                            formErrors.sellingPrice && 
                            <p className='text-[14px] leading-5 text-[#DC2626]'>{formErrors.sellingPrice}</p>
                        }
                    </div>

                    <div className="h-14 flex items-center gap-1 px-2 py-[6px] bg-[#F5F5F5] border border-[#EAEAEA] rounded-lg focus-within:border-primaryColor">
                        <div className="w-6 h-6 flex items-center justify-center">
                            <img src={Rupee} alt="" />
                        </div>

                        <input 
                            id='sellingPrice' 
                            name='sellingPrice' 
                            className='w-[calc(100%-24px)] outline-none bg-transparent font-normal text-base text-[#121212]' 
                            type="number" 
                            placeholder='0.00' 
                            value={pricesAndTaxes.sellingPrice} 
                            onChange={handleChange} 
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-x-2 gap-y-4">
                <Input 
                    labelText='HSN Code' 
                    errorText={formErrors.hsnCode} 
                    inputOptions={{
                        type: 'text',
                        id: 'hsnCode',
                        name: 'hsnCode',
                        placeholder: 'enter hsn code',
                        value: pricesAndTaxes.hsnCode,
                        onChange: handleChange
                    }}
                    required
                />

                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <h1 className='w-fit select-none cursor-pointer font-medium text-base text-[#121212]'>Select Tax Code<span className="text-[#DC2626]">*</span></h1>
                    
                        {
                            formErrors.taxCode && 
                            <p className='text-[14px] leading-5 text-[#DC2626]'>{formErrors.taxCode}</p>
                        }
                    </div>

                    <div onClick={toggleGstDropdown} ref={gstDropdownButtonRef} className={`relative cursor-pointer h-14 px-3 py-4 border border-[#EAEAEA] bg-[#F5F5F5] rounded-[9px] flex gap-[10px] ${gstDropdownOpen && 'border-[#00c853]'}`}>
                        <p className='select-none w-[calc(100%-24px)] font-normal text-base text-[#666666] whitespace-nowrap overflow-x-scroll no-scrollbar'>
                            {Object.keys(pricesAndTaxes.taxCode).length > 0 ? pricesAndTaxes.taxCode.codeName : 'select tax code'}
                        </p>

                        <div className="w-6 h-6 flex items-center justify-center">
                            <img className='select-none' src={ChevronDown} alt="" />
                        </div>

                        {
                            gstDropdownOpen && 
                            <div ref={gstDropdownRef} className="absolute z-10 top-14 left-0 w-full border border-[#EAEAEA] rounded-xl overflow-hidden shadow-[0px_0px_12px_0px_#0000003D]">
                                {gstCodes.map(code => {
                                    return (
                                        <div key={code.id} onClick={(e) => handleDropdownOptionClick(e, code)} className="w-full px-3 py-4 flex gap-[10px] bg-[#FFFFFF] hover:bg-[#F5F5F5]">
                                            <div className={`w-6 h-6 rounded-md ${pricesAndTaxes.taxCode.id === code.id ? 'border-none bg-primaryAccent flex items-center justify-center' : 'border border-[#D1D5DB]'}`}>
                                                <img className='select-none' src={pricesAndTaxes.taxCode.id === code.id ? TickMark : undefined} alt="" />
                                            </div>

                                            <p className='font-medium select-none text-base text-[#666666]'>{code.codeName}</p>
                                        </div>
                                    )
                                })}
                            </div>
                        }
                    </div>
                </div>
            </div>

            <Input 
                labelText='Country' 
                errorText={formErrors.country} 
                inputOptions={{
                    type: 'text',
                    id: 'country',
                    name: 'country',
                    placeholder: 'Enter country',
                    value: pricesAndTaxes.country,
                    onChange: handleChange,
                    disabled: true
                }}
            />
        </div>
    );
}

export default PricesAndTaxes;
